import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import Flag from 'react-world-flags';
import Avatar from '../../components/Avatar';
import ExpertLeagueSportRecommendationsBarChart, { BreakdownItem } from '../../components/expertComponents/ExpertLeagueSportRecommendationsBarChart';
import ExpertPostingRhythmHeatmap, { PostingRhythmCell } from '../../components/expertComponents/ExpertPostingRhythmHeatmap';
import ExpertRoiTimelineChart, { TimePeriod } from '../../components/expertComponents/ExpertRoiTimelineChart';
import ExpertStatMiniCard from '../../components/expertComponents/ExpertStatMiniCard';
import ExpertStatsTopStripeCard from '../../components/expertComponents/ExpertStatsTopStripeCard';
import ExpertFollowButton from '../../components/ExpertFollowButton';
import Dropdown from '../../components/Dropdown';
import { ROUTES } from '../../constants';
import { avatarConfigDisplayColors } from '../../constants/avatarPalette';
import { useAuth } from '../../contexts/AuthContext';
import { useCountUp } from '../../hooks/useCountUp';
import { followApi } from '../../services/followService';
import { League, Sport } from '../../services/sportsLeaguesService';
import { tipsterApi } from '../../services/tipsterService';
import '../../styles/expert-stats.scss';
import { TipsterPublic } from '../../types/interfaces';
import { apiGet } from '../../utils/api';
import { useTranslation } from '../../contexts/TranslationContext';
import { TranslationKeys } from '../../translations';
import { translateEvent } from '../../utils/translateEvent';

interface StatsFilters {
  sportId: string;
  leagueId: string;
  minOdds: string;
  maxOdds: string;
  minStake: string;
  tier: string;
}

interface FeedItem {
  id: number;
  bet_event_id: number;
  tipster_id: number;
  tipster_tier_id?: number | null;
  tipster_description?: string | null;
  stake?: number | null;
  bet_event?: {
    id: number;
    odds: number;
    event: string;
    result?: string;
    game?: {
      id: number;
      datetime: string;
      home_team: string;
      away_team: string;
      league?: { id: number; name: string };
    };
  };
  tipster_tier?: { id: number; level: number; name?: string } | null;
}

interface FeedResponse {
  items: FeedItem[];
  total: number;
  page: number;
  page_size: number;
  shuffle_seed: string;
}

interface RoiTimelinePoint {
  time: string;
  roi_at_this_time: number;
}

interface RecommendationMetrics {
  total_picks: number;
  picks_won: number;
  win_rate: number | null;
  avg_odds: number | null;
  roi: number | null;
  cashProfit: number;
  roi_timeline?: RoiTimelinePoint[];
  sport_breakdown?: BreakdownItem[];
  league_breakdown?: BreakdownItem[];
  picks_per_week_last_4w?: number;
  picks_with_description?: number;
  last_pick_at?: string | null;
  posting_rhythm?: PostingRhythmCell[];
}

function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function getDateRange(period: TimePeriod): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date(to);
  switch (period) {
    case '7d':
      from.setDate(from.getDate() - 7);
      break;
    case '30d':
      from.setDate(from.getDate() - 30);
      break;
    case '3m':
      from.setMonth(from.getMonth() - 3);
      break;
    case 'ytd':
      from.setMonth(0);
      from.setDate(1);
      break;
    case 'all':
      from.setFullYear(2020);
      from.setMonth(0);
      from.setDate(1);
      break;
  }
  return { from, to };
}

function formatLastPick(
  isoStr: string | null | undefined,
  t: TranslationKeys,
): { value: string; sub: string | undefined } {
  if (!isoStr) return { value: '—', sub: undefined };
  const d = new Date(isoStr);
  const diffMs = Date.now() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  const sub = d.toLocaleDateString();
  if (diffDays === 0) return { value: t.common.today, sub };
  if (diffDays === 1) return { value: t.common.yesterday, sub };
  if (diffDays < 7) return { value: t.common.daysAgo.replace('{days}', String(diffDays)), sub };
  if (diffDays < 30) return { value: t.common.weeksAgo.replace('{weeks}', String(Math.floor(diffDays / 7))), sub };
  return { value: d.toLocaleDateString(), sub: undefined };
}

function buildMetricsUrl(tipsterId: number, f: StatsFilters, period: TimePeriod): string {
  const p = new URLSearchParams();
  p.set('tier', f.tier);
  if (f.sportId) p.set('sport_id', f.sportId);
  if (f.leagueId) p.set('league_id', f.leagueId);
  if (f.minOdds) p.set('min_odds', f.minOdds);
  if (f.maxOdds) p.set('max_odds', f.maxOdds);
  if (f.minStake) p.set('min_stake', f.minStake);
  const { from, to } = getDateRange(period);
  p.set('from_date', toDateStr(from));
  p.set('to_date', toDateStr(to));
  return `/tipsters/${tipsterId}/recommendations/metrics?${p.toString()}`;
}

const emptyFilters = (): StatsFilters => ({
  sportId: '',
  leagueId: '',
  minOdds: '',
  maxOdds: '',
  minStake: '',
  tier: 'free',
});

const FEED_PAGE_SIZE = 25;

function buildFeedUrl(tipsterId: number, f: StatsFilters, page: number): string {
  const p = new URLSearchParams();
  p.set('tipster_id', String(tipsterId));
  p.set('page', String(page));
  p.set('page_size', String(FEED_PAGE_SIZE));
  p.set('tier', f.tier);
  p.set('from_date', '2020-01-01');
  p.set('sort_by', 'game_date_desc');
  if (f.sportId) p.set('sport_id', f.sportId);
  if (f.leagueId) p.set('league_id', f.leagueId);
  if (f.minOdds) p.set('min_odds', f.minOdds);
  if (f.maxOdds) p.set('max_odds', f.maxOdds);
  if (f.minStake) p.set('min_stake', f.minStake);
  return `/tipsters/recommendations/feed?${p.toString()}`;
}

const RESULT_COLORS: Record<string, { color: string; bg: string }> = {
  WIN:   { color: '#3fb950', bg: 'rgba(63,185,80,0.12)' },
  LOOSE: { color: '#f85149', bg: 'rgba(248,81,73,0.12)' },
  VOID:  { color: '#d29922', bg: 'rgba(210,153,34,0.10)' },
};

function formatPickDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const ExpertStatsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const resultConfig: Record<string, { label: string; color: string; bg: string }> = {
    WIN:   { label: t.pickResults.won,     ...RESULT_COLORS.WIN },
    LOOSE: { label: t.pickResults.lost,    ...RESULT_COLORS.LOOSE },
    VOID:  { label: t.pickResults.void,    ...RESULT_COLORS.VOID },
  };

  const [tipster, setTipster] = useState<TipsterPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<RecommendationMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const [filters, setFilters] = useState<StatsFilters>(emptyFilters());
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30d');
  const [chartView, setChartView] = useState<'roi' | 'breakdown'>('roi');
  const [sports, setSports] = useState<Sport[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loadingLeagues, setLoadingLeagues] = useState(false);

  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedTotal, setFeedTotal] = useState(0);
  const [feedPage, setFeedPage] = useState(1);

  const oddsMinRef = useRef<HTMLInputElement>(null);
  const oddsMaxRef = useRef<HTMLInputElement>(null);
  const stakeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;
    const tipsterId = parseInt(id);
    Promise.all([
      tipsterApi.getById(tipsterId),
      followApi.getFollowedIds().catch(() => [] as number[]),
    ])
      .then(([tipsterData, followedIds]) => {
        setTipster(tipsterData);
        setIsFollowing(followedIds.includes(tipsterId));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    tipsterApi.getSports(parseInt(id), filters.tier)
      .then(setSports)
      .catch(() => setSports([]));
  }, [id, filters.tier]);

  useEffect(() => {
    if (!id || !filters.sportId) {
      setLeagues([]);
      return;
    }
    setLoadingLeagues(true);
    tipsterApi.getLeagues(parseInt(id), parseInt(filters.sportId), filters.tier)
      .then(setLeagues)
      .catch(() => setLeagues([]))
      .finally(() => setLoadingLeagues(false));
  }, [id, filters.sportId, filters.tier]);

  const fetchFeed = useCallback(async (tipsterId: number, f: StatsFilters, page: number) => {
    setFeedLoading(true);
    try {
      const data = await apiGet<FeedResponse>(buildFeedUrl(tipsterId, f, page));
      setFeedItems(data.items);
      setFeedTotal(data.total);
    } catch {
      setFeedItems([]);
      setFeedTotal(0);
    } finally {
      setFeedLoading(false);
    }
  }, []);

  const fetchMetrics = useCallback(async (tipsterId: number, f: StatsFilters, period: TimePeriod) => {
    setMetricsLoading(true);
    try {
      const data = await apiGet<RecommendationMetrics>(buildMetricsUrl(tipsterId, f, period));
      setMetrics(data);
    } catch {
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    fetchMetrics(parseInt(id), filters, timePeriod);
  }, [id, filters, timePeriod, fetchMetrics]);

  useEffect(() => {
    if (!id) return;
    fetchFeed(parseInt(id), filters, feedPage);
  }, [id, filters, feedPage, fetchFeed]);

  const updateFilter = (key: keyof StatsFilters, value: string) => {
    setFilters(prev => {
      if (prev[key] === value) return prev;
      const next = { ...prev, [key]: value };
      if (key === 'sportId') next.leagueId = '';
      return next;
    });
    setFeedPage(1);
  };

  const commitOddsStake = () => {
    setFilters(prev => {
      const minOdds = oddsMinRef.current?.value ?? prev.minOdds;
      const maxOdds = oddsMaxRef.current?.value ?? prev.maxOdds;
      const minStake = stakeRef.current?.value ?? prev.minStake;
      if (minOdds === prev.minOdds && maxOdds === prev.maxOdds && minStake === prev.minStake) return prev;
      return { ...prev, minOdds, maxOdds, minStake };
    });
  };

  const handleReset = () => {
    if (oddsMinRef.current) oddsMinRef.current.value = '';
    if (oddsMaxRef.current) oddsMaxRef.current.value = '';
    if (stakeRef.current) stakeRef.current.value = '';
    setFilters(emptyFilters());
    setFeedPage(1);
  };

  const handleToggleFollow = async () => {
    if (!tipster || followLoading) return;
    if (!isAuthenticated) {
      navigate(
        `${ROUTES.LOGIN}?redirect=${encodeURIComponent(id ? `/experts/${id}/stats` : ROUTES.EXPERTS_SEARCH)}`
      );
      return;
    }
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await followApi.unfollow(tipster.id);
        setIsFollowing(false);
        setTipster(p => p ? { ...p, followers_count: p.followers_count - 1 } : p);
      } else {
        await followApi.follow(tipster.id);
        setIsFollowing(true);
        setTipster(p => p ? { ...p, followers_count: p.followers_count + 1 } : p);
      }
    } catch { /* silently */ } finally {
      setFollowLoading(false);
    }
  };

  const totalPicksAnim = useCountUp(metrics?.total_picks ?? null);
  const winRateAnim = useCountUp(metrics?.win_rate ?? null);
  const avgOddsAnim = useCountUp(metrics?.avg_odds ?? null);
  const roiAnim = useCountUp(metrics?.roi ?? null);

  if (loading) return <div className="expert-stats">{t.common.loading}</div>;
  if (!tipster) return <div className="expert-stats">{t.common.expertNotFound}</div>;

  const isFiltered =
    !!filters.sportId || !!filters.leagueId || !!filters.minOdds ||
    !!filters.maxOdds || !!filters.minStake || filters.tier !== 'free';

  return (
    <section className="expert-stats">
      <button className="expert-stats__back" onClick={() => navigate(-1)} aria-label={t.common.back}>
        <FiArrowLeft size={14} /> {t.common.back}
      </button>
      <div className="expert-stats__top-container">
        <div className="expert-stats__header">
          <div className="expert-stats__profile-info">
            <div className="expert-stats__avatar">
              <Avatar
                matrix={tipster.avatar_matrix!}
                {...avatarConfigDisplayColors(tipster.avatar_config!)}
                compact
              />
            </div>
            <div className="expert-stats__identity">
              <div className="expert-stats__name-row">
                <h1 className="expert-stats__name">
                  {tipster.full_name || t.common.expertNumber.replace('{id}', String(tipster.id))}
                </h1>
                {tipster.is_verified && (
                  <FiCheckCircle size={16} className="expert-stats__verified" />
                )}
              </div>
              <div className="expert-stats__meta">
                {tipster.country && (
                  <span className="expert-stats__country-pill">
                    <Flag code={tipster.country} className="expert-stats__flag" />
                    <span>{tipster.country}</span>
                  </span>
                )}
                <span className="expert-stats__followers">
                  {t.expertStats.followers.replace('{count}', String(tipster.followers_count))}
                </span>
              </div>
            </div>
          </div>
          <div className="expert-stats__header-buttons">
            <ExpertFollowButton
              isFollowing={isFollowing}
              followersCount={tipster.followers_count}
              disabled={followLoading}
              onClick={handleToggleFollow}
            />
          </div>
        </div>
      </div>

      <div className="expert-stats__top-stripe">
        <div className="expert-stats__top-stripe__filters">
          <div className="stats-filters">
            <span className="stats-filters__label">{t.expertStats.filters}</span>
            <div className="stats-filters__scroll">
              <button
                className={`stats-filters__reset-pill${isFiltered ? ' stats-filters__reset-pill--active' : ''}`}
                onClick={handleReset}
                type="button"
              >
                {t.common.reset}
              </button>
              <div className="stats-filters__pill">
                <span className="stats-filters__pill-label">{t.expertStats.sport}</span>
                <Dropdown
                  className="stats-filters__select"
                  value={filters.sportId}
                  onChange={(sportId) => updateFilter('sportId', sportId)}
                  placeholder={t.filters.all}
                  title={t.expertStats.sport}
                  options={[
                    { value: '', label: t.filters.all },
                    ...sports.map((s) => ({
                      value: String(s.id),
                      label: s.name,
                      icon: s.icon,
                    })),
                  ]}
                />
              </div>

              <div className="stats-filters__pill">
                <span className="stats-filters__pill-label">{t.expertStats.league}</span>
                <Dropdown
                  className="stats-filters__select"
                  value={filters.leagueId}
                  onChange={(leagueId) => updateFilter('leagueId', leagueId)}
                  placeholder={t.filters.all}
                  title={t.expertStats.league}
                  disabled={!filters.sportId || loadingLeagues}
                  options={[
                    { value: '', label: t.filters.all },
                    ...leagues.map((l) => ({
                      value: String(l.id),
                      label: l.name,
                    })),
                  ]}
                />
              </div>

              <div className="stats-filters__pill">
                <span className="stats-filters__pill-label">{t.expertStats.tier}</span>
                <select
                  className="stats-filters__select"
                  value={filters.tier}
                  onChange={e => updateFilter('tier', e.target.value)}
                >
                  <option value="free">{t.common.free}</option>
                  <option value="all">All</option>
                </select>
              </div>

              <div className="stats-filters__pill stats-filters__pill--range">
                <span className="stats-filters__pill-label">{t.expertStats.odds}</span>
                <input
                  ref={oddsMinRef}
                  className="stats-filters__range-input"
                  type="number"
                  min="1"
                  step="0.05"
                  placeholder="1.00"
                  defaultValue={filters.minOdds}
                  onBlur={commitOddsStake}
                />
                <span className="stats-filters__range-sep">—</span>
                <input
                  ref={oddsMaxRef}
                  className="stats-filters__range-input"
                  type="number"
                  min="1"
                  step="0.05"
                  placeholder="∞"
                  defaultValue={filters.maxOdds}
                  onBlur={commitOddsStake}
                />
              </div>

              <div className="stats-filters__pill">
                <span className="stats-filters__pill-label">{t.expertStats.stakeMin}</span>
                <input
                  ref={stakeRef}
                  className="stats-filters__range-input"
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="0"
                  defaultValue={filters.minStake}
                  onBlur={commitOddsStake}
                />
                <span className="stats-filters__unit">u</span>
              </div>
            </div>

            <button
              className={`stats-filters__reset${isFiltered ? ' stats-filters__reset--active' : ''}`}
              onClick={handleReset}
              type="button"
            >
              {t.common.reset}
            </button>
          </div>
        </div>

        <div className={`expert-stats__top-stripe__main-stats loading-zone${metricsLoading ? ' loading-zone--active' : ''}`}>
          <ExpertStatsTopStripeCard
            title={t.expertStats.totalPicks}
            value={totalPicksAnim !== null ? Math.round(totalPicksAnim).toString() : '—'}
          />
          <ExpertStatsTopStripeCard
            title={t.expertStats.winRate}
            value={winRateAnim !== null ? `${winRateAnim.toFixed(1)}%` : '—'}
          />
          <ExpertStatsTopStripeCard
            title={t.expertStats.avgOdds}
            value={avgOddsAnim !== null ? avgOddsAnim.toFixed(2) : '—'}
          />
          <ExpertStatsTopStripeCard
            title={t.common.roi}
            value={roiAnim !== null
              ? `${roiAnim >= 0 ? '+' : ''}${roiAnim.toFixed(1)}%`
              : '—'}
          />
        </div>
      </div>


      <div className="expert-stats__subsection-one">
        <div className="card expert-stats__subsection-chart-container">
          <div className="expert-stats__subsection-chart-container__header">
            <div className="expert-stats__subsection-chart-container__header__title">
              {chartView === 'roi'
                ? t.expertStats.performance
                : filters.sportId
                  ? t.expertStats.picksByLeague
                  : t.expertStats.picksBySport}
            </div>
            <div className="chart-picker">
              <button
                className={`chart-picker__btn${chartView === 'roi' ? ' chart-picker__btn--active' : ''}`}
                onClick={() => setChartView('roi')}
                type="button"
              >
                {t.expertStats.roiTimeline}
              </button>
              <button
                className={`chart-picker__btn${chartView === 'breakdown' ? ' chart-picker__btn--active' : ''}`}
                onClick={() => setChartView('breakdown')}
                type="button"
              >
                {t.expertStats.picksBreakdown}
              </button>
            </div>
          </div>

          {chartView === 'roi' ? (
            <ExpertRoiTimelineChart
              data={metrics?.roi_timeline ?? []}
              cashProfit={metrics?.cashProfit ?? 0}
              timePeriod={timePeriod}
              onTimePeriodChange={setTimePeriod}
              fromDate={getDateRange(timePeriod).from}
              toDate={getDateRange(timePeriod).to}
            />
          ) : (
            <ExpertLeagueSportRecommendationsBarChart
              sportBreakdown={metrics?.sport_breakdown ?? []}
              leagueBreakdown={metrics?.league_breakdown ?? []}
              sportFiltered={!!filters.sportId}
              timePeriod={timePeriod}
              onTimePeriodChange={setTimePeriod}
              fromDate={getDateRange(timePeriod).from}
              toDate={getDateRange(timePeriod).to}
            />
          )}
        </div>

        <div className={`expert-stats__subsection-right-col loading-zone${metricsLoading ? ' loading-zone--active' : ''}`}>
          <div className="expert-stats__subsection-stats-container">
            {(() => {
              const leagues = metrics?.league_breakdown ?? [];
              const topRoi = leagues.filter(l => l.roi != null && l.roi > 0)
                .sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0))[0];
              const mostPlayed = [...leagues].sort((a, b) => b.count - a.count)[0];
              const topLeague = topRoi ?? mostPlayed;
              const topLeagueLabel = topRoi ? t.expertStats.topLeague : t.expertStats.mostPlayedLeague;
              const topLeagueSub = topRoi
                ? t.expertStats.roiPercent.replace('{roi}', topRoi.roi!.toFixed(1))
                : mostPlayed ? t.expertStats.picksCount.replace('{count}', String(mostPlayed.count)) : '—';
              return (
                <div className="card expert-stats__stat-card">
                  <ExpertStatMiniCard
                    label={topLeagueLabel}
                    value={topLeague?.label ?? '—'}
                    sub={topLeagueSub}
                  />
                </div>
              );
            })()}
            <div className="card expert-stats__stat-card">
              <ExpertStatMiniCard
                label={t.expertStats.picksPerWeek}
                value={(metrics?.picks_per_week_last_4w ?? 0).toString()}
                sub={t.expertStats.last4Weeks}
              />
            </div>
            <div className="card expert-stats__stat-card">
              <ExpertStatMiniCard
                label={t.expertStats.withDescription}
                value={(metrics?.picks_with_description ?? 0).toString()}
                sub={metrics && metrics.total_picks > 0
                  ? t.expertStats.percentOfPicks.replace(
                      '{percent}',
                      String(Math.round((metrics.picks_with_description ?? 0) / metrics.total_picks * 100)),
                    )
                  : undefined}
              />
            </div>
            <div className="card expert-stats__stat-card">
              {(() => {
                const lp = formatLastPick(metrics?.last_pick_at, t);
                return (
                  <ExpertStatMiniCard
                    label={t.expertStats.lastPick}
                    value={lp.value}
                    sub={lp.sub}
                  />
                );
              })()}
            </div>
          </div>

          <div className="card">
            <ExpertPostingRhythmHeatmap data={metrics?.posting_rhythm ?? []} />
          </div>
        </div>

        <div className="card expert-stats__subsection-bottom-card picks-table">
          <div className="picks-table__header-row">
            <span>{t.expertStats.date}</span>
            <span>{t.expertStats.match}</span>
            <span className="picks-table__col--right">{t.expertStats.odds}</span>
            <span className="picks-table__col--right">{t.expertStats.stake}</span>
            <span>{t.expertStats.result}</span>
          </div>

          {feedLoading && feedItems.length === 0 && (
            <div className="picks-table__empty">{t.expertStats.loadingPicks}</div>
          )}
          {!feedLoading && feedItems.length === 0 && (
            <div className="picks-table__empty">{t.expertStats.noPicksFound}</div>
          )}
          <div className={`loading-zone${feedLoading && feedItems.length > 0 ? ' loading-zone--active' : ''}`}>
          {feedItems.map((item) => {
            const game = item.bet_event?.game;
            const result = item.bet_event?.result ?? null;
            const odds = item.bet_event?.odds ?? 0;
            const resCfg = result ? resultConfig[result] : null;
            const isPending = !result || result === 'TO_RESOLVE' || result === 'UNKNOWN';

            return (
              <div key={item.id} className="picks-table__row">
                <div className="picks-table__date-col">
                  <span className="picks-table__date">
                    {game?.datetime ? formatPickDate(game.datetime) : '—'}
                  </span>
                  {game?.league?.name && (
                    <span className="picks-table__league-chip">{translateEvent(game.league.name, t.eventsDictionary)}</span>
                  )}
                </div>
                <span className="picks-table__match">
                  <span className="picks-table__matchup">
                    {game ? `${game.home_team} vs ${game.away_team}` : '—'}
                  </span>
                  <span className="picks-table__event">{translateEvent(item.bet_event?.event ?? '', t.eventsDictionary)}</span>
                </span>
                <span className="picks-table__odds picks-table__col--right">
                  {odds.toFixed(2)}
                </span>
                <span className="picks-table__stake picks-table__col--right">
                  {item.stake != null ? item.stake : '—'}
                </span>
                <span className="picks-table__result">
                  {isPending
                    ? <span className="picks-table__result-chip picks-table__result-chip--pending">{t.pickResults.pending}</span>
                    : resCfg
                      ? <span
                          className="picks-table__result-chip"
                          style={{ color: resCfg.color, background: resCfg.bg }}
                        >{resCfg.label}</span>
                      : '—'
                  }
                </span>
              </div>
            );
          })}
          </div>

          {feedTotal > 0 && (
            <div className="picks-table__pagination">
              <button
                type="button"
                className="button_secondary"
                disabled={feedPage <= 1}
                onClick={() => setFeedPage(p => p - 1)}
              >
                {t.common.previous}
              </button>
              <span className="picks-table__pagination-info">
                {t.common.paginationShort
                  .replace('{page}', String(feedPage))
                  .replace('{totalPages}', String(Math.ceil(feedTotal / FEED_PAGE_SIZE)))}
              </span>
              <button
                type="button"
                className="button_secondary"
                disabled={feedPage >= Math.ceil(feedTotal / FEED_PAGE_SIZE)}
                onClick={() => setFeedPage(p => p + 1)}
              >
                {t.common.next}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ExpertStatsPage;
